import React, { useState } from 'react';
import { Layout } from '@/components/Layout';
import { usePageSeo } from '@/hooks/usePageSeo';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Mail, MapPin, Info } from 'lucide-react';
import TutorialModal from '@/components/ui/TutorialModal';

const Contact = () => {
  const [isOpen, setIsOpen] = useState(false);

  usePageSeo({
    title: 'Contact PropertyTaxHero | Get in Touch',
    description:
      'Contact PropertyTaxHero for questions about our free property tax appeal tool. PropertyTaxHero is operated by YourWayHome.com LLC in Broomfield, Colorado.',
    path: '/contact',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact PropertyTaxHero',
      url: 'https://propertytaxhero.com/contact',
      mainEntity: {
        '@type': 'Organization',
        name: 'PropertyTaxHero',
        url: 'https://propertytaxhero.com',
        email: 'support@propertytaxhero.com',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '2205 136th Ave, Suite 105 #107',
          addressLocality: 'Broomfield',
          addressRegion: 'CO',
          postalCode: '80023',
          addressCountry: 'US',
        },
      },
    },
  });

  return (
    <Layout>
      <article className="container py-12 md:py-20 max-w-4xl mx-auto">
        <h1 className="text-3xl md:text-4xl font-bold text-tax-deepBlue mb-6">
          Contact PropertyTaxHero
        </h1>
        <p className="text-lg text-tax-neutralGray mb-10 leading-relaxed">
          PropertyTaxHero is the official free property tax appeal tool for
          homeowners across all 50 states. Below you'll find our contact
          information and business details. For help, please start by watching
          our{' '}
          <button onClick={() => setIsOpen(true)} className="text-tax-deepBlue">
            video tutorial
          </button>
        </p>

        <section className="mb-10">
          <div className="flex items-center mb-4">
            <Mail className="text-tax-brightBlue mr-2 flex-shrink-0" />
            <h2 className="text-2xl font-semibold text-tax-deepBlue">Email</h2>
          </div>
          <p className="text-tax-neutralGray mb-2">For general inquiries:</p>
          <p className="text-tax-neutralGray">
            <a
              href="mailto:support@propertytaxhero.com"
              className="text-tax-brightBlue hover:underline font-medium"
            >
              support@propertytaxhero.com
            </a>
          </p>
          <p className="text-sm text-tax-neutralGray mt-2">
            Please note: PropertyTaxHero is a self-service platform and does not
            offer direct customer support for individual property tax appeals.
          </p>
        </section>

        <section className="mb-10">
          <div className="flex items-center mb-4">
            <MapPin className="text-tax-brightBlue mr-2 flex-shrink-0" />
            <h2 className="text-2xl font-semibold text-tax-deepBlue">
              Business Address
            </h2>
          </div>
          <p className="text-tax-neutralGray">
            PropertyTaxHero is operated by YourWayHome.com LLC
          </p>
          <p className="text-tax-neutralGray mt-2">
            2205 136th Ave, Suite 105 #107
            <br />
            Broomfield, CO 80023
            <br />
            United States
          </p>
        </section>

        <section className="mb-10">
          <div className="flex items-center mb-4">
            <Info className="text-tax-brightBlue mr-2 flex-shrink-0" />
            <h2 className="text-2xl font-semibold text-tax-deepBlue">
              About Our Service
            </h2>
          </div>
          <p className="text-tax-neutralGray mb-4">
            PropertyTaxHero helps homeowners check for overassessment,
            understand appeal deadlines, and prepare property tax appeals —
            completely free. We do not charge fees, and we do not sell user
            data.
          </p>
          <p className="text-tax-neutralGray">
            The only official PropertyTaxHero website is{' '}
            <a
              href="https://propertytaxhero.com"
              className="text-tax-brightBlue hover:underline font-medium"
            >
              propertytaxhero.com
            </a>
            .
          </p>
        </section>

        <section className="text-center bg-tax-lightGray rounded-lg p-8">
          <h2 className="text-2xl font-semibold text-tax-deepBlue mb-3">
            Ready to check your property assessment?
          </h2>
          <p className="text-tax-neutralGray mb-6">
            It's free and takes just a few minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="bg-tax-deepBlue hover:bg-tax-brightBlue">
              <Link to="/#property-form">Get Started Free</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/about">About Us</Link>
            </Button>
          </div>
        </section>
      </article>
      <TutorialModal isOpen={isOpen} setIsOpen={() => setIsOpen(false)} />
    </Layout>
  );
};

export default Contact;
